
import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    NgZone,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { NotificationType } from 'app/core/enums/generated/NotificationType';
import { WidgetTypeEnum } from 'app/core/enums/generated/WidgetTypeEnum';
import { SystemMessage } from 'app/core/system-message/system-message-service';
import { UserWidgetCoordinateVm } from 'app/shared/generated/Administration/Models/Users/UserWidgetCoordinateVm';
import { GlobalService } from 'app/shared/services/global.service';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { OrgQuickNavWidgetComponent } from '../widgets/org-quick-nav-widget/org-quick-nav-widget.component';
import { ResizableMessagesWidget } from '../widgets/resizable-messages-widget/resizable-messages-widget';
import { ResizableNotificationsWidget } from '../widgets/resizable-notifications-widget/resizable-notifications-widget.component';
import { ResizableRecallsWidget } from '../widgets/resizable-recalls-widget/resizable-recalls-widget.component';
import { ResizableRefillRequest } from '../widgets/resizable-refill-request/resizable-refill-request';
import { RxSearchWidget } from '../widgets/rx-search-widget/rx-search-widget';
import { SupportTicketWidgetComponent } from '../widgets/support-ticket-widget/support-ticket-widget.component';
import { SysInvCycleCountWidgetComponent } from '../widgets/sys-inv-cycle-count-widget/sys-inv-cycle-count-widget.component';
import { ExpiringMedsWidgetComponent } from '../widgets/expiring-meds-widget/expiring-meds-widget.component';
import { ResizableNdcLookupWidgetComponent } from '../widgets/resizable-ndc-lookup-widget/resizable-ndc-lookup-widget.component';
import { InsulinRequiresReviewWidgetComponent } from '../widgets/insulin-requires-review-widget/insulin-requires-review-widget.component';
import { InsulinAwaitingRxWidgetComponent } from '../widgets/insulin-awaiting-rx-widget/insulin-awaiting-rx-widget.component';
import { InsulinQueueSummaryWidgetComponent } from '../widgets/insulin-queue-summary-widget/insulin-queue-summary-widget.component';
import { IssuanceQuickScanWidgetComponent } from '../widgets/issuance-quick-scan-widget/issuance-quick-scan-widget.component';
import { SecurityService } from 'app/core/security/security.service';
import { DASHBOARD_WIDGET_CACHE_KEY_PREFIX } from './dashboard-widget-cache';

/**
 * Customizable drag-and-drop widget dashboard.
 *
 * Widgets are positioned on an absolute-positioned grid whose cell width scales
 * with the container (cell height is fixed at {@link GRID_ROW_HEIGHT} px).
 * Users can drag widgets to reposition and resize them via corner handles.
 * Coordinates are persisted to the server on mouseup (debounced at 400 ms).
 *
 * **Caching**: Widget coordinates are cached in `sessionStorage` so subsequent
 * page loads render instantly without waiting for the API. The cache is
 * refreshed transparently in the background on every load.
 *
 * **Responsive scaling**: When the viewport changes, all widget positions and
 * widths are recalculated proportionally to the new grid cell width. This keeps
 * layouts visually consistent across window sizes.
 *
 * To populate the dashboard with widgets, go to the "Widgets" section of your
 * user profile and toggle the desired widget. Active widgets appear as green on
 * the "Widgets" page.
 *
 * On mobile (≤ 959 px) the grid is disabled and widgets stack vertically.
 */
@Component({
    selector: 'pcg-custom-widget-dashboard',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ResizableNotificationsWidget,
        ResizableRecallsWidget,
        ResizableMessagesWidget,
        ResizableRefillRequest,
        RxSearchWidget,
        SupportTicketWidgetComponent,
        OrgQuickNavWidgetComponent,
        SysInvCycleCountWidgetComponent,
        ExpiringMedsWidgetComponent,
        ResizableNdcLookupWidgetComponent,
        InsulinRequiresReviewWidgetComponent,
        InsulinAwaitingRxWidgetComponent,
        InsulinQueueSummaryWidgetComponent,
        IssuanceQuickScanWidgetComponent
    ],
    templateUrl: './custom-widget-dashboard.component.html',
    styleUrl: './custom-widget-dashboard.component.scss',
})
export class CustomWidgetDashboard implements OnInit, AfterViewInit {

    // #region Injections
    private readonly http = inject(HttpClient);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly zone = inject(NgZone);
    private readonly sec = inject(SecurityService);
    private readonly containerRef = viewChild.required<ElementRef<HTMLElement>>('dashboardContainer');
    // #endregion

    // #region Internal state
    private container!: HTMLElement;
    private resizeObserver: ResizeObserver | null = null;

    /** Cached container rect — refreshed once per drag/resize start to avoid repeated reflows. */
    private cachedContainerRect: DOMRect | null = null;

    private draggedItem: HTMLElement | null = null;
    private resizingItem: HTMLElement | null = null;
    private dragOffset = { x: 0, y: 0 };
    private resizeStart = { x: 0, y: 0, width: 0, height: 0 };

    /** Subject that debounces coordinate save requests to avoid duplicate backend hits. */
    private readonly saveCoordinates$ = new Subject<UserWidgetCoordinateVm>();

    private get cacheKey(): string { return `${DASHBOARD_WIDGET_CACHE_KEY_PREFIX}:${this.sec.getUser()?.id}`; }
    // #endregion

    // #region Grid configuration
    /** Fixed grid row height in pixels. */
    readonly GRID_ROW_HEIGHT = 25;
    /** Number of virtual columns the container is divided into. Higher = finer grid. */
    private readonly GRID_COLUMNS = 75;
    /** Minimum widget dimensions in pixels. */
    private readonly MIN_WIDGET_WIDTH = 300;
    private readonly MIN_WIDGET_HEIGHT = 200;
    // #endregion

    // #region Public state (template-bound)
    gridWidth = 25;
    notificationTypeEnum = NotificationType;
    userWidgetCoordinates = signal<UserWidgetCoordinateVm[]>([]);

    readonly WIDGET = WidgetTypeEnum;
    /** Widgets in this enum DO NOT have a component associated with them */
    readonly UNSUPPORTED_WIDGETS = [WidgetTypeEnum.SysInvShortcuts];
    /** Widgets that are looped through in the template to render */
    dashboardWidgets = computed(() => {
        const cords = this.userWidgetCoordinates()
            .filter(c => !this.UNSUPPORTED_WIDGETS.includes(c.widgetType));
        return cords.map(
            c => ({...c,
                // Height is saved in units but components need ACTUAL pixels (unit * height size)
                actualHeight: c.height! * this.GRID_ROW_HEIGHT 
            })
        );
    });

    isMobile = signal(false);
    isUpdatingWidget = signal(false);
    // #endregion

    ngOnInit() {
        this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
        this.setupSavePipeline();
    }

    ngAfterViewInit() {
        this.container = this.containerRef().nativeElement;
        this.recalculateGridWidth();

        // Render from cache immediately so widgets appear without waiting for the API.
        const cached = this.loadFromCache();
        if (cached?.length) {
            this.applyWidgetCoordinates(cached);
        }

        // Always fetch fresh data; updates cache and corrects any stale positions.
        this.http.get<SystemMessage>('api/Admin/Widgets/GetUserWidgetCoordinates').subscribe((msg: SystemMessage) => {
            this.saveToCache(msg.model);
            this.applyWidgetCoordinates(msg.model);
        });

        this.setupResizeObserver();
    }

    // #region Mouse event handlers (bound via host)

    /** Initiates a drag or resize operation depending on the click target. */
    handleMouseDown(e: MouseEvent): void {
        if (this.isMobile()) { return; }
        const target = e.target as HTMLElement;

        // Resize handle click — use closest() for robust widget association.
        if (target.classList.contains('resize-handle')) {
            this.isUpdatingWidget.set(true);
            const itemElement = target.closest('.dashboard-item') as HTMLElement;
            if (itemElement) {
                this.resizingItem = itemElement;
                const rect = itemElement.getBoundingClientRect();
                this.resizeStart = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height };
                this.cachedContainerRect = this.container.getBoundingClientRect();
                this.container.classList.add('dashboard-container-dragging');
            }
            return;
        }

        if (!target.closest('.drag-area')) { return; }

        // Dashboard item click → start drag
        const item = target.closest('.dashboard-item') as HTMLElement;
        if (item) {
            this.draggedItem = item;
            const rect = item.getBoundingClientRect();
            this.dragOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top };
            this.cachedContainerRect = this.container.getBoundingClientRect();
        }
    }

    /** Moves or resizes the active widget, snapping to the grid. */
    handleMouseMove(e: MouseEvent): void {
        // Resize
        if (this.resizingItem) {
            this.isUpdatingWidget.set(true);
            const deltaX = e.clientX - this.resizeStart.x;
            const deltaY = e.clientY - this.resizeStart.y;

            let newWidth = this.resizeStart.width + deltaX;
            let newHeight = this.resizeStart.height + deltaY;

            // Snap to grid
            newWidth = Math.max(this.gridWidth, Math.round(newWidth / this.gridWidth) * this.gridWidth);
            newHeight = Math.max(this.GRID_ROW_HEIGHT, Math.round(newHeight / this.GRID_ROW_HEIGHT) * this.GRID_ROW_HEIGHT);
            
            if (newWidth <= this.MIN_WIDGET_WIDTH || newHeight <= this.MIN_WIDGET_HEIGHT) { return; }

            this.resizingItem.style.width = `${newWidth}px`;
            this.resizingItem.style.height = `${newHeight}px`;
            this.updateWidgetHeight(this.resizingItem.id, newHeight);
            this.cdr.markForCheck();
            return;
        }

        // Drag
        if (this.draggedItem && this.cachedContainerRect) {
            this.isUpdatingWidget.set(true);
            this.draggedItem.classList.add('dragging');
            this.container.classList.add('dashboard-container-dragging');

            let newX = (e.clientX - this.cachedContainerRect.left) - this.dragOffset.x;
            let newY = (e.clientY - this.cachedContainerRect.top) - this.dragOffset.y;

            // Snap to grid
            newX = Math.round(newX / this.gridWidth) * this.gridWidth;
            newY = Math.round(newY / this.GRID_ROW_HEIGHT) * this.GRID_ROW_HEIGHT;

            // Keep within container bounds
            const itemRect = this.draggedItem.getBoundingClientRect();
            newX = Math.max(0, Math.min(newX, this.cachedContainerRect.width - itemRect.width));
            newY = Math.max(0, newY);

            this.draggedItem.style.left = `${newX}px`;
            this.draggedItem.style.top = `${newY}px`;
        }
    }

    /** Finalises a drag/resize and persists the new coordinates (debounced). */
    handleMouseUp(_e: MouseEvent): void {
        this.isUpdatingWidget.set(false);
        if (this.draggedItem) {
            this.container.classList.remove('dashboard-container-dragging');
            this.draggedItem.classList.remove('dragging');

            const userWidget = this.userWidgetCoordinates()?.find(o => o.widgetType == this.draggedItem!.id);
            if (userWidget) {
                const xCoord = Math.round(Number(this.draggedItem.style.left.split('px')[0]) / this.gridWidth);
                const yCoord = Math.round(Number(this.draggedItem.style.top.split('px')[0]) / this.GRID_ROW_HEIGHT);
                userWidget.x = xCoord;
                userWidget.y = yCoord;

                this.saveCoordinates$.next({
                    userWidgetId: userWidget.userWidgetId,
                    x: xCoord,
                    y: yCoord,
                });
            }
            this.draggedItem = null;
        }

        if (this.resizingItem) {
            this.container.classList.remove('dashboard-container-dragging');

            const userWidget = this.userWidgetCoordinates()?.find(o => o.widgetType == this.resizingItem!.id);
            if (userWidget) {
                const width = Math.round(Number(this.resizingItem.style.width.split('px')[0]) / this.gridWidth);
                const height = Math.round(Number(this.resizingItem.style.height.split('px')[0]) / this.GRID_ROW_HEIGHT);
                userWidget.height = height;
                userWidget.width = width;

                this.saveCoordinates$.next({
                    userWidgetId: userWidget.userWidgetId,
                    height,
                    width,
                });
            }
            this.resizingItem = null;
        }

        this.cachedContainerRect = null;
        this.fitContainerHeight();
    }
    // #endregion

    // #region Private helpers

    /**
     * Takes a list of widget coordinates, sets visibility flags, forces
     * change detection so `@if` blocks create the DOM elements, then
     * positions each widget on the grid.
     */
    private applyWidgetCoordinates(coordinates: UserWidgetCoordinateVm[]): void {
        this.userWidgetCoordinates.set(coordinates);


        // Step 2: Synchronous CD so the DOM elements actually exist before positioning.
        this.cdr.detectChanges();

        // Step 3: Now position each widget on the grid.
        this.userWidgetCoordinates()?.forEach(w =>
            this.positionWidget(w.widgetType.toString(), w.x!, w.y!, w.width!, w.height!),
        );

        this.fitContainerHeight();
        this.cdr.markForCheck();
    }

    /**
     * Debounced save pipeline — collapses rapid-fire save calls (e.g. quick
     * drag → resize) into a single backend POST. Also updates the session cache.
     */
    private setupSavePipeline(): void {
        this.saveCoordinates$.pipe(
            debounceTime(400),
            tap(() => this.saveToCache(this.userWidgetCoordinates())),
            switchMap(model => this.http.post<SystemMessage>('api/Admin/Widgets/SaveCoordinates', model)),
        ).subscribe();

        this.destroyRef.onDestroy(() => this.saveCoordinates$.complete());
    }

    /** Sets up a ResizeObserver to reflow widgets when the container width changes. */
    private setupResizeObserver(): void {
        this.zone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver(() => {
                this.recalculateGridWidth();
                this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
                this.cdr.markForCheck();
            });
            this.resizeObserver.observe(this.container);
        });

        this.destroyRef.onDestroy(() => {
            this.resizeObserver?.disconnect();
            this.resizeObserver = null;
        });
    }

    /** Positions a single widget element on the grid by its numeric DOM id. */
    private positionWidget(id: string, x: number, y: number, width: number, height: number): void {
        const element = document.getElementById(id);
        if (!element) { return; }

        element.style.left = `${x * this.gridWidth}px`;
        element.style.top = `${y * this.GRID_ROW_HEIGHT}px`;
        element.style.width = `${width * this.gridWidth}px`;
        element.style.height = `${height * this.GRID_ROW_HEIGHT}px`;
        this.updateWidgetHeight(element.id, height * this.GRID_ROW_HEIGHT);
    }

    /**
     * Recalculates the grid cell width based on current container width,
     * then repositions **and resizes** all widgets proportionally so the
     * layout scales smoothly when the viewport changes.
     */
    private recalculateGridWidth(): void {
        const containerRect = this.container.getBoundingClientRect();
        const boxWidth = containerRect.width / this.GRID_COLUMNS;
        this.container.style.backgroundSize = `${boxWidth}px ${this.GRID_ROW_HEIGHT}px`;
        this.gridWidth = boxWidth;

        this.userWidgetCoordinates()?.forEach((widget: UserWidgetCoordinateVm) => {
            const el = document.getElementById(widget.widgetType.toString());
            if (el) {
                el.style.left = `${widget.x! * this.gridWidth}px`;
                el.style.width = `${widget.width! * this.gridWidth}px`;
            }
        });

        this.fitContainerHeight();
    }

    /**
     * Calculates the bottom-most widget edge and sets the container height
     * to fit all widgets with a small gutter — avoids excessive empty space
     * while ensuring widgets aren't clipped.
     */
    private fitContainerHeight(): void {
        if (this.isMobile()) { return; }

        const GUTTER = this.GRID_ROW_HEIGHT * 4; // ~100 px breathing room below last widget
        let maxBottom = 0;

        this.userWidgetCoordinates()?.forEach((widget: UserWidgetCoordinateVm) => {
            const bottom = (widget.y! + widget.height!) * this.GRID_ROW_HEIGHT;
            if (bottom > maxBottom) { maxBottom = bottom; }
        });

        // Ensure at least the viewport height so it doesn't look empty with few widgets
        const minHeight = window.innerHeight - this.container.getBoundingClientRect().top;
        this.container.style.height = `${Math.max(maxBottom + GUTTER, minHeight)}px`;
    }

    /** Updates the height input property for the widget matching the given DOM element id. */
    private updateWidgetHeight(elementId: string, height: number): void {
        const userCords = this.userWidgetCoordinates();
        const updated = userCords.map(c => {
            if (c.widgetType.toString() == elementId) 
            {
                c.height = height / this.GRID_ROW_HEIGHT;
            }
            return c;
        });
        this.userWidgetCoordinates.set(updated);
    }
    // #endregion

    // #region Cache helpers

    /** Writes widget coordinates to sessionStorage for instant rendering on next load. */
    private saveToCache(coordinates: UserWidgetCoordinateVm[]): void {
        try {
            sessionStorage.setItem(this.cacheKey, JSON.stringify(coordinates));
        } catch { /* quota exceeded — non-critical */ }
    }

    /** Reads cached widget coordinates from sessionStorage (returns null on miss). */
    private loadFromCache(): UserWidgetCoordinateVm[] | null {
        try {
            const raw = sessionStorage.getItem(this.cacheKey);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    }
    // #endregion
}
